import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import styles from './PdfViewer.module.scss';
import Button from '../../Button';
import { Text } from '../../typography';
import Icon from '../../Icon';
import { Form, Spinner } from 'react-bootstrap';
import { customColors } from '../../../utilities';
import {
  buildTextIndex,
  findPagesWithTerm,
} from '../../../utilities/pdf_export/pdfSearch';
import TextWithIcon from '../../typography/TextWithIcon';
import 'react-pdf/dist/cjs/Page/AnnotationLayer.css';
import 'react-pdf/dist/cjs/Page/TextLayer.css';
import { useIsMobile } from '../../../hooks/misc';

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
const SEARCH_PAGE_LIMIT = 1500;

export default function PdfViewer({ file, width }) {
  const src = useMemo(
    () => file?.url ?? file?.imageUrl,
    [file?.imageUrl, file?.url],
  );

  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pageInput, setPageInput] = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [containerWidth, setContainerWidth] = useState(null);
  const [fullScreen, setFullScreen] = useState(false);

  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageTexts, setPageTexts] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [isIndexing, setIsIndexing] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const isMobile = useIsMobile();

  const wrapperRef = useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      if (wrapperRef.current) {
        setContainerWidth(wrapperRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    if (!src) return;
    setNumPages(null);
    setCurrentPage(1);
    setPageInput('');
    setPageLoading(true);

    setShowSearch(false);
    setSearchTerm('');
    setPageTexts(null);
    setSearchResults([]);
    setCurrentResultIndex(0);
    setIsIndexing(false);
    setSearchError(null);
  }, [src]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handlePageRendered = () => {
    setPageLoading(false);
  };

  const effectiveContainerWidth = containerWidth ?? width ?? 800;
  const baseWidth = width || effectiveContainerWidth - 32;
  const pageWidth = baseWidth * zoom;

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.1, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.3));
  const handleZoomReset = () => setZoom(1);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const { scrollWidth, clientWidth } = wrapper;
    if (scrollWidth <= clientWidth) {
      wrapper.scrollLeft = 0;
    } else {
      wrapper.scrollLeft = (scrollWidth - clientWidth) / 2;
    }
  }, [zoom, currentPage, containerWidth, fullScreen]);

  const goToPage = (page) => {
    if (!numPages) return;
    const p = Math.min(Math.max(page, 1), numPages);
    setCurrentPage(p);
    setPageLoading(true);
  };

  const handleNextPage = () => goToPage(currentPage + 1);
  const handlePrevPage = () => goToPage(currentPage - 1);
  const handleLastPage = () => goToPage(numPages);
  const handleFirstPage = () => goToPage(1);

  const handlePageInputChange = (e) => setPageInput(e.target.value);

  const handleGoToPage = (e) => {
    e.preventDefault();
    if (!numPages) return;
    const pageNumber = parseInt(pageInput, 10);
    if (Number.isNaN(pageNumber)) return;
    goToPage(pageNumber);
  };

  const toggleSearch = () => {
    setShowSearch((prev) => !prev);
    if (showSearch) {
      setSearchTerm('');
      setSearchResults([]);
      setCurrentResultIndex(0);
      setSearchError(null);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSearchError(null);
    if (!value) {
      setSearchResults([]);
      setCurrentResultIndex(0);
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults([]);
      setCurrentResultIndex(0);
      setSearchError(null);
      return;
    }

    try {
      setSearchError(null);

      let texts = pageTexts;
      if (!texts) {
        setIsIndexing(true);
        const idx = await buildTextIndex(src);
        texts = idx?.pageTexts || idx;
        setPageTexts(texts);
        setIsIndexing(false);
      }

      const pages = findPagesWithTerm(texts, term) || [];
      setSearchResults(pages);
      setCurrentResultIndex(0);

      if (pages.length > 0) {
        goToPage(pages[0]);
      } else {
        setSearchError('No matches found');
      }
    } catch (err) {
      console.error('Search error:', err);
      setIsIndexing(false);
      setSearchError('Search failed');
    }
  };

  const hasResults = searchResults && searchResults.length > 0;

  const handleNextResult = () => {
    if (!hasResults) return;
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    goToPage(searchResults[nextIndex]);
  };

  const handlePrevResult = () => {
    if (!hasResults) return;
    const prevIndex =
      (currentResultIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentResultIndex(prevIndex);
    goToPage(searchResults[prevIndex]);
  };
  const customTextRenderer = React.useCallback(
    ({ str }) => {
      if (!searchTerm) return str;
      try {
        const re = new RegExp(`${escapeRegExp(searchTerm)}`, 'gi');

        return str.replace(re, (m) => `<mark class="pdf-hl">${m}</mark>`);
      } catch {
        return str;
      }
    },
    [searchTerm],
  );

  const handlePageRenderError = (err) => {
    if (
      err?.name === 'AbortException' ||
      /textLayer task cancelled/i.test(err?.message || '')
    ) {
      return; // ignore expected cancellations
    }
    console.error('PDF page render error:', err);
  };

  if (!src) return null;

  return (
    <div className={fullScreen ? styles.fullScreen : styles.container}>
      <div className={fullScreen ? styles.toolbarFullScreen : styles.toolbar}>
        <div className={styles.zoomControls}>
          <Text
            noMargin
            size="lg"
            color="accentPrimary"
            onClick={handleZoomOut}
            hover
          >
            -
          </Text>
          <Text noMargin>{Math.round(zoom * 100)}%</Text>
          <Text
            hover
            noMargin
            size="lg"
            color="accentPrimary"
            onClick={handleZoomIn}
          >
            +
          </Text>
          <Icon
            hover
            style={{ fontSize: '1.2rem', cursor: 'pointer' }}
            color="accentPrimary"
            noMargin
            onClick={handleZoomReset}
          >
            refresh
          </Icon>
        </div>

        {numPages && (
          <>
            <div className={styles.pageControls}>
              <Icon
                hover
                disabled={currentPage === 1}
                onClick={handleFirstPage}
              >
                first_page
              </Icon>
              <Button
                value="Prev"
                size="xs"
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
              />
              <Text noMargin className={styles.pageDisplay}>
                {currentPage} / {numPages}
              </Text>
              <Button
                value="Next"
                size="xs"
                type="button"
                onClick={handleNextPage}
                disabled={currentPage >= numPages}
              />
              <Icon
                disabled={currentPage === numPages}
                onClick={handleLastPage}
              >
                last_page
              </Icon>
            </div>

            <div className={styles.pageControls}>
              <Text size="sm" noMargin>
                Page
              </Text>
              <Form onSubmit={handleGoToPage} className={styles.goToForm}>
                <Form.Control
                  type="number"
                  min={1}
                  max={numPages}
                  value={pageInput}
                  onChange={handlePageInputChange}
                  placeholder={`${currentPage}`}
                  className={styles.pageInput}
                />
                <Button value="Go" size="sm" disabled={!pageInput} />
              </Form>
            </div>
          </>
        )}

        <div>
          <div className={styles.pageControls}>
            {!isMobile && (
              <Icon
                onClick={() => setFullScreen(!fullScreen)}
                hover
                style={{ color: customColors.BLUE }}
              >
                {fullScreen ? 'fullscreen_exit' : 'fullscreen'}
              </Icon>
            )}
            <Icon onClick={toggleSearch} hover>
              {!!showSearch ? 'search_off' : 'search'}
            </Icon>
          </div>
        </div>
      </div>

      {showSearch && (
        <div className={styles.searchContainer}>
          <div className={styles.searchForm}>
            <Form className={styles.searchForm} onSubmit={handleSearchSubmit}>
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search document"
                className={styles.searchInput}
              />
              <Button
                value={isIndexing ? 'Indexing…' : 'Search'}
                size="sm"
                type="submit"
                disabled={!searchTerm || isIndexing}
              />

              {searchTerm && (
                <Icon
                  hover
                  className={styles.clearSearchIcon}
                  onClick={() => {
                    setSearchTerm('');
                    setSearchResults([]);
                    setCurrentResultIndex(0);
                    setSearchError(null);
                  }}
                >
                  refresh
                </Icon>
              )}
            </Form>

            {hasResults && (
              <div className={styles.searchResultsInfo}>
                <Text size="xs" noMargin>
                  {currentResultIndex + 1} of {searchResults.length}
                </Text>
                <Icon hover onClick={handlePrevResult}>
                  keyboard_arrow_up
                </Icon>
                <Icon hover onClick={handleNextResult}>
                  keyboard_arrow_down
                </Icon>
              </div>
            )}
          </div>
          {numPages >= SEARCH_PAGE_LIMIT && isIndexing && (
            <TextWithIcon
              icon="report_problem"
              iconProps={{ color: customColors.ORANGE }}
              size="sm"
              noMargin
              color="orange"
              textAlign="center"
            >
              Search may be slow due to large file size...
            </TextWithIcon>
          )}
          {searchError && (
            <Text noMargin textAlign="center" color="danger">
              {searchError}
            </Text>
          )}
        </div>
      )}

      <div className={styles.documentWrapper} ref={wrapperRef}>
        {pageLoading && !numPages && (
          <div className={styles.spinnerOverlay}>
            <Spinner animation="border" className={styles.spinner} />
          </div>
        )}
        <div
          className={
            fullScreen || isMobile ? styles.fullScreenDocument : styles.document
          }
        >
          <Document
            file={src}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(err) => {
              console.error('PDF load error:', err);
            }}
          >
            {numPages && (
              <Page
                key={`${currentPage}-${zoom}-${searchTerm}`}
                pageNumber={currentPage}
                width={pageWidth}
                renderTextLayer={true}
                renderAnnotationLayer={false}
                onRenderSuccess={handlePageRendered}
                onRenderError={handlePageRenderError}
                customTextRenderer={customTextRenderer}
              />
            )}
          </Document>
        </div>
      </div>
    </div>
  );
}
